import { Box, Paper, Stack, Typography } from '@mui/material';
import { useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import {
  formatNumber,
  isEmptyArray,
  isPositive,
  isString,
} from '@vy/utils';
import { getBarChartColors } from '../../../../../../../../utils/charts/bar-chart/get-bar-chart-colors';

function getCustomerLabel(label: string | null | undefined, index: number): string {
  if (isString(label)) {
    return label;
  }

  return `Customer ${index + 1}`;
}

export const ProductUsageBreakdownChartTooltip: React.FC = () => {
  const tooltipData = useAxesTooltip();

  if (!tooltipData || tooltipData.length === 0) {
    return null;
  }

  const { axisValue, seriesItems } = tooltipData[0];

  const itemsWithData = seriesItems.filter((item) => {
    return typeof item.value === 'number' && isPositive(item.value as number);
  });

  const noDataForCurrentBar = isEmptyArray(itemsWithData);

  if (noDataForCurrentBar) {
    return null;
  }

  const title = `${axisValue} - Usage Breakdown`;

  return (
    <Paper
      sx={{
        backgroundColor: '#FFFFFF',
        transform: 'translate(0%, -50%)',
        minWidth: '250px',
        maxWidth: '350px',
        position: 'relative',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Stack sx={{
        gap: 0
      }}>
        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: "24px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            color: "#1a1a1a",
            padding: "16px 16px 0px 16px"
          }}>
          {title}
        </Typography>

        <Stack sx={{
          padding: "8px 0"
        }}>
          {itemsWithData.map((item, index) => {
            const value = item.value;
            if (typeof value !== 'number') {
              return null;
            }

            const productName = getCustomerLabel(item.formattedLabel, index);
            const colorIndex = seriesItems.findIndex((s) => s.seriesId === item.seriesId);

            return (
              <Stack
                key={String(item.seriesId)}
                direction="row"
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "2px 16px",
                  gap: 2
                }}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: 0
                  }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '2px',
                      backgroundColor: getBarChartColors(colorIndex),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      color: "#374151",
                      maxWidth: '180px'
                    }}>
                    {productName}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: 500,
                    color: "#1a1a1a",
                    textAlign: "right"
                  }}>
                  {formatNumber(value ?? 0)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};
